import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  CircularProgress,
  Divider,
  Chip,
  Tabs,
  Tab,
  Pagination,
} from "@mui/material";

import {
  Star,
  StarBorder,
  MoreVert,
  Email,
  Phone,
  Flag,
  WorkspacePremium,
  TrendingUp,
  History,
  EmojiEvents,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";

import { useEffect, useState } from "react";
import UserApi from "../../../apis/user.api";
import propertyDefaultImg from "../../../assets/images/property-default-image.jpg";

export default function UserDetails() {
  const navigate = useNavigate();
  const { id: userId } = useParams();
  const userApi = new UserApi();

  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewTab, setReviewTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setReviewTab(newValue);
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuClick = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const COMMENTS_PER_PAGE = 5;

  const [tenantPage, setTenantPage] = useState(1);
  const [ownerPage, setOwnerPage] = useState(1);

  const [tenantTab, setTenantTab] = useState(0);

  const handleTenantTabChange = (event, newValue) => {
    setTenantTab(newValue);
  };

  const [trustSummary, setTrustSummary] = useState(null);
  const [eventsData, setEventsData] = useState({ events: [], pagination: { page: 1, limit: 5, total: 0, pages: 1 } });
  const [eventsPage, setEventsPage] = useState(1);
  const [loadingTrust, setLoadingTrust] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await userApi.searchPeopleDetail(userId);

        if (res.data.status === "success") {
          const data = res.data.data;

          const formatted = {
            id: userId,
            name: data.name,
            email: data.email,
            phone: data.phone,
            avatar: data.profilePicture || "",
            rating: data.reviewsAsOwner?.ratingSummary?.avgOverallRating || 0,

            surveyAnswers:
              data.questionaire?.map((q) => ({
                question: q.question,
                answer: q.answer,
              })) || [],

            ownedProperties:
              data.properties?.map((p) => ({
                id: p.property_id,
                name: p.name,
                photo: p.images?.[0] || propertyDefaultImg,
                location: `${p.city}, ${p.state}`,
              })) || [],

            // NEW DATA
            currentTenant: data.currentTenant || null,

            previousTenant:
              data.previouslyTenantAt?.map((p) => ({
                propertyId: p.propertyId,
                propertyName: p.propertyName,
                propertyImage: p.propertyImages?.[0] || propertyDefaultImg,
                owner: p.owner || null,
                startDate: p.startDate,
                endDate: p.endDate,
              })) || [],

            reviewsAsTenant: data.reviewsAsTenant || {},
            reviewsAsOwner: data.reviewsAsOwner || {},
          };

          setPerson(formatted);
        }
      } catch (e) {
        console.log("Error loading details:", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const fetchTrustSummary = async () => {
      try {
        setLoadingTrust(true);
        const res = await userApi.getAdminTenantRatingSummary(userId);
        if (res.data.status === "success") {
          setTrustSummary(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching rating summary:", err);
      } finally {
        setLoadingTrust(false);
      }
    };
    fetchTrustSummary();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const fetchTrustEvents = async () => {
      try {
        const res = await userApi.getAdminTenantRatingEvents(userId, eventsPage, 5);
        if (res.data.status === "success") {
          setEventsData(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching rating events:", err);
      }
    };
    fetchTrustEvents();
  }, [userId, eventsPage]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!person) return <Typography>User Not Found</Typography>;

  const tenantComments = person.reviewsAsTenant?.comments || [];

  const paginatedTenantComments = tenantComments.slice(
    (tenantPage - 1) * COMMENTS_PER_PAGE,
    tenantPage * COMMENTS_PER_PAGE
  );

  const ownerComments = person.reviewsAsOwner?.comments || [];

  const paginatedOwnerComments = ownerComments.slice(
    (ownerPage - 1) * COMMENTS_PER_PAGE,
    ownerPage * COMMENTS_PER_PAGE
  );

  return (
    <Box sx={{ width: "100%", maxWidth: 1300, mx: "auto", mt: 3 }}>

      <Grid container spacing={4}>
        {/* LEFT SIDE: PROFILE SUMMARY */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Avatar
                src={person.avatar}
                sx={{ width: 120, height: 120, mx: "auto", mb: 2 }}
              />
              <Typography variant="h5" fontWeight={700}>
                {person.name}
              </Typography>

            </Box>

            <Divider sx={{ my: 2 }} />

            {/* CONTACT INFO */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Contact Information
              </Typography>

              {person.email && (
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Email sx={{ mr: 1, color: "primary.main" }} />
                  <Typography>{person.email}</Typography>
                </Box>
              )}

              {person.phone && (
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Phone sx={{ mr: 1, color: "primary.main" }} />
                  <Typography>{person.phone}</Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* RIGHT SIDE CONTENT */}
        <Grid item xs={12} md={8}>
          {/* SURVEY QUESTIONS */}
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              User Questionnaire
            </Typography>

            {person.surveyAnswers.length === 0 && (
              <Typography>No Survey Responses</Typography>
            )}

            <Grid container spacing={2}>
              {person.surveyAnswers.map((ans, i) => (
                <Grid item xs={12} key={i}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "#F8F9FA",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {ans.question}
                    </Typography>

                    <Chip
                      label={ans.answer}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Card>

          {/* TRUST SCORE & BADGES DASHBOARD */}
          <Card
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              background: "linear-gradient(135deg, #ffffff 0%, #f9fbfb 100%)",
              border: "1px solid #e3f2f5"
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <WorkspacePremium sx={{ color: "#033E4A", fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700} color="#033E4A">
                  Tenant Trust Score & Badges
                </Typography>
              </Box>
              {trustSummary?.rank && (
                <Chip
                  icon={<Star sx={{ color: "#ffb300 !important" }} />}
                  label={`Rank #${trustSummary.rank}`}
                  sx={{
                    bgcolor: "#fff8e1",
                    color: "#b78103",
                    fontWeight: "bold",
                    border: "1px solid #ffe082"
                  }}
                />
              )}
            </Box>

            {loadingTrust ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={30} />
              </Box>
            ) : !trustSummary?.rating ? (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                No trust score details or history found for this user.
              </Typography>
            ) : (
              <Grid container spacing={3}>
                {/* Score Circle & Stats */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      boxShadow: "0 2px 12px rgba(3, 62, 74, 0.04)",
                      border: "1px solid #f0f6f7",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%"
                    }}
                  >
                    <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
                      <CircularProgress
                        variant="determinate"
                        value={Math.min(100, ((trustSummary.rating.totalScore || 0) / 500) * 100)}
                        size={120}
                        thickness={5}
                        sx={{
                          color: (trustSummary.rating.totalScore || 0) >= 100 ? "#2e7d32" : (trustSummary.rating.totalScore || 0) >= 50 ? "#ed6c02" : "#d32f2f",
                          transform: "rotate(-90deg)"
                        }}
                      />
                      <Box
                        sx={{
                          top: 0,
                          left: 0,
                          bottom: 0,
                          right: 0,
                          position: 'absolute',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography variant="h4" component="div" fontWeight={800} color="#033E4A">
                          {trustSummary.rating.totalScore || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Trust Points
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="subtitle2" color="text.secondary" align="center" sx={{ mb: 1 }}>
                      Current Streak: <strong>{trustSummary.rating.currentStreak?.count || 0} months</strong>
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center", mt: 1 }}>
                      {trustSummary.pointsSummary && (
                        <>
                          <Chip
                            icon={<TrendingUp sx={{ color: "#2e7d32 !important" }} />}
                            label={`+${trustSummary.pointsSummary.totalPositive || 0}`}
                            size="small"
                            variant="outlined"
                            color="success"
                          />
                          <Chip
                            icon={<TrendingUp sx={{ color: "#d32f2f !important", transform: "rotate(90deg)" }} />}
                            label={`${trustSummary.pointsSummary.totalNegative || 0}`}
                            size="small"
                            variant="outlined"
                            color="error"
                          />
                        </>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Badges Section */}
                <Grid item xs={12} md={7}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      boxShadow: "0 2px 12px rgba(3, 62, 74, 0.04)",
                      border: "1px solid #f0f6f7",
                      height: "100%"
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="#033E4A" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <EmojiEvents sx={{ fontSize: 20, color: "#ed6c02" }} /> Active Badges
                    </Typography>

                    {!trustSummary.badges || trustSummary.badges.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                        No badges earned yet.
                      </Typography>
                    ) : (
                      <Grid container spacing={2}>
                        {trustSummary.badges.map((b, idx) => {
                          const levelColor =
                            b.level === "GOLD" ? "#ffb300" : b.level === "SILVER" ? "#9e9e9e" : "#cd7f32";
                          const levelBg =
                            b.level === "GOLD" ? "#fff8e1" : b.level === "SILVER" ? "#f5f5f5" : "#efebe9";

                          return (
                            <Grid item xs={12} key={idx}>
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  border: `1px solid ${levelColor}40`,
                                  bgcolor: levelBg,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: levelColor,
                                    width: 40,
                                    height: 40,
                                    fontSize: "1.2rem",
                                    boxShadow: "0 2px 5px rgba(0,0,0,0.15)"
                                  }}
                                >
                                  {b.displayInfo?.icon || "🏆"}
                                </Avatar>
                                <Box>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography variant="subtitle2" fontWeight={700} color="#033E4A">
                                      {b.displayInfo?.name || b.badgeType}
                                    </Typography>
                                    <Chip
                                      label={b.level}
                                      size="small"
                                      sx={{
                                        height: 16,
                                        fontSize: "0.65rem",
                                        fontWeight: "bold",
                                        bgcolor: levelColor,
                                        color: "#ffffff"
                                      }}
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                    {b.displayInfo?.description}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                                    Earned: {new Date(b.earnedAt).toLocaleDateString()}
                                  </Typography>
                                </Box>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </Box>
                </Grid>

                {/* Rating Events Timeline */}
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: "#ffffff",
                      boxShadow: "0 2px 12px rgba(3, 62, 74, 0.04)",
                      border: "1px solid #f0f6f7",
                      mt: 1
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700} color="#033E4A" sx={{ mb: 2, display: "flex", alignItems: "center", gap: 0.5 }}>
                      <History sx={{ fontSize: 20, color: "#033E4A" }} /> Event History Log
                    </Typography>

                    {!eventsData.events || eventsData.events.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                        No events recorded.
                      </Typography>
                    ) : (
                      <>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {eventsData.events.map((e, idx) => {
                            const isPositive = e.points > 0;
                            return (
                              <Box
                                key={e._id || idx}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  bgcolor: "#fcfdfe",
                                  border: "1px solid #edf3f4",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}
                              >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                  {isPositive ? (
                                    <CheckCircle sx={{ color: "#2e7d32" }} />
                                  ) : (
                                    <Cancel sx={{ color: "#d32f2f" }} />
                                  )}
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight={600} color="#033E4A">
                                      {e.description || e.eventType}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(e.eventDate).toLocaleString()}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Chip
                                  label={isPositive ? `+${e.points}` : `${e.points}`}
                                  color={isPositive ? "success" : "error"}
                                  size="small"
                                  sx={{ fontWeight: "bold" }}
                                />
                              </Box>
                            );
                          })}
                        </Box>

                        {eventsData.pagination && eventsData.pagination.pages > 1 && (
                          <Box display="flex" justifyContent="center" mt={3}>
                            <Pagination
                              count={eventsData.pagination.pages}
                              page={eventsPage}
                              onChange={(event, val) => setEventsPage(val)}
                              color="primary"
                              size="small"
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
          </Card>

          {/* Current / Previous Tenant At */}
          <Card sx={{ mt: 4, p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Stay History
            </Typography>

            <Tabs value={tenantTab} onChange={handleTenantTabChange} sx={{ mb: 3 }}>
              <Tab label="Current Stays" />
              <Tab label="Previous Stays" />
            </Tabs>

            {/* CURRENT TENANT */}
            {tenantTab === 0 && (
              <>
                {!person.currentTenant ? (
                  <Typography>No Current Tenancy</Typography>
                ) : (
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      maxWidth: 320,
                      boxShadow: "0px 4px 15px rgba(0,0,0,0.08)",
                    }}
                  >
                    <img
                      src={
                        person.currentTenant.propertyImages?.[0] ||
                        propertyDefaultImg
                      }
                      style={{
                        width: "100%",
                        height: 180,
                        objectFit: "cover",
                      }}
                    />

                    <Box sx={{ p: 2 }}>
                      <Typography variant="h6" fontWeight={700}>
                        {person.currentTenant.propertyName}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {new Date(
                          person.currentTenant.startDate
                        ).toLocaleDateString()}{" "}
                        →{" "}
                        {new Date(
                          person.currentTenant.endDate
                        ).toLocaleDateString()}
                      </Typography>

                      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                        <Chip
                          label={`Rent ₹${person.currentTenant.donePrice}`}
                          color="success"
                          size="small"
                        />
                        <Chip
                          label={`Deposit ₹${person.currentTenant.securityDeposit}`}
                          color="primary"
                          size="small"
                        />
                      </Box>

                      {/* OWNER INFO */}
                      <Divider sx={{ mb: 1 }} />

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Avatar
                          src={person.currentTenant.owner?.profilePicture}
                          sx={{ width: 40, height: 40 }}
                        />

                        <Box>
                          <Typography fontWeight={600}>
                            {person.currentTenant.owner?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {person.currentTenant.owner?.email},  {person.currentTenant.owner?.phone}
                          </Typography>

                        </Box>
                      </Box>
                    </Box>
                  </Card>
                )}
              </>
            )}

            {/* PREVIOUS TENANT */}
            {tenantTab === 1 && (
              <>
                {person.previousTenant.length === 0 ? (
                  <Typography>No Previous Tenancy</Typography>
                ) : (
                  <Grid container spacing={3}>
                    {person.previousTenant.map((p) => (
                      <Grid item xs={12} sm={6} md={6} key={p.propertyId}>
                        <Card
                          sx={{
                            borderRadius: 3,
                            overflow: "hidden",
                            height: "100%",
                            boxShadow: "0px 3px 12px rgba(0,0,0,0.08)",
                          }}
                        >
                          <img
                            src={p.propertyImages?.[0] || propertyDefaultImg}
                            style={{
                              width: "100%",
                              height: 150,
                              objectFit: "cover",
                            }}
                          />

                          <Box sx={{ p: 2 }}>
                            <Typography fontWeight={700}>
                              {p.propertyName}
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1 }}
                            >
                              {new Date(p.startDate).toLocaleDateString()} →{" "}
                              {new Date(p.endDate).toLocaleDateString()}
                            </Typography>

                            {/* OWNER INFO */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                mt: 1,
                              }}
                            >
                              <Avatar
                                src={p.owner?.profilePicture}
                                sx={{ width: 32, height: 32 }}
                              />

                              <Box>
                                <Typography fontWeight={800}>
                                  {p?.owner?.name || "Unknown Owner"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {p.owner?.email},  {p.owner?.phone}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Card>

          {/* User Reviews */}
          <Card sx={{ mt: 4, p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              User Reviews
            </Typography>

            <Tabs
              value={reviewTab}
              onChange={handleTabChange}
              sx={{ mb: 2 }}
            >
              <Tab label="As Tenant" />
              <Tab label="As Owner" />
            </Tabs>

            {/* TENANT REVIEWS */}
            {reviewTab === 0 && (
              <>
                <Typography variant="body2">
                  Total Reviews: {person.reviewsAsTenant?.ratingSummary?.totalReviews}
                </Typography>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  Avg Rating: {person.reviewsAsTenant?.ratingSummary?.avgOverallRating}
                </Typography>

                {person.reviewsAsTenant?.comments?.length === 0 && (
                  <Typography>No Reviews</Typography>
                )}

                {paginatedTenantComments.map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: "#F8F9FA",
                    }}
                  >
                    <Typography fontWeight={600}>{c.fullName}</Typography>
                    <Typography variant="body2">
                      {c.comment || "No Comment"}
                    </Typography>
                  </Box>
                ))}

                {tenantComments.length > COMMENTS_PER_PAGE && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={Math.ceil(tenantComments.length / COMMENTS_PER_PAGE)}
                      page={tenantPage}
                      onChange={(e, val) => setTenantPage(val)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}

            {/* OWNER REVIEWS */}
            {reviewTab === 1 && (
              <>
                <Typography variant="body2">
                  Total Reviews: {person.reviewsAsOwner?.ratingSummary?.totalReviews}
                </Typography>

                <Typography variant="body2" sx={{ mb: 2 }}>
                  Avg Rating: {person.reviewsAsOwner?.ratingSummary?.avgOverallRating}
                </Typography>

                {paginatedOwnerComments.map((c, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 2,
                      mb: 1,
                      borderRadius: 2,
                      bgcolor: "#F8F9FA",
                    }}
                  >
                    <Typography fontWeight={600}>{c.fullName}</Typography>
                    <Typography variant="body2">
                      {c.comment || "No Comment"}
                    </Typography>
                  </Box>
                ))}

                {ownerComments.length > COMMENTS_PER_PAGE && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={Math.ceil(ownerComments.length / COMMENTS_PER_PAGE)}
                      page={ownerPage}
                      onChange={(e, val) => setOwnerPage(val)}
                      color="primary"
                    />
                  </Box>
                )}
              </>
            )}
          </Card>

          {/* PROPERTIES */}
          <Card
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Owned Properties
            </Typography>

            <Grid container spacing={3}>
              {person.ownedProperties.length === 0 && (
                <Typography style={{ marginTop: "20px", marginLeft: "24px" }}>No Properties Found</Typography>
              )}

              {person.ownedProperties.map((prop) => (
                <Grid item xs={12} sm={6} md={4} key={prop.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      overflow: "hidden",
                      boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                      height: "100%",
                    }}
                  >
                    <img
                      src={prop.photo}
                      alt={prop.name}
                      style={{
                        width: "100%",
                        height: 150,
                        objectFit: "cover",
                      }}
                    />
                    <Box sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {prop.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {prop.location}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Card>
        </Grid>
      </Grid>

      {/* BACK BUTTON */}
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#033E4A",
            color: "white",
            padding: "12px 30px",
            borderRadius: 50,
            border: "none",
            cursor: "pointer",
            fontSize: "15px",
          }}
        >
          Back
        </button>
      </Box>
    </Box>
  );
}
